import { graphHooks } from '@tunasong/graph-lib/react'
import { PlayList as PlayListIcon } from '@tunasong/icons'
import { isPlayList } from '@tunasong/models'
import type { RenderMenuItemProps } from '@tunasong/plugin-lib'
import { isEntity, isPersisted } from '@tunasong/schemas'
import type { ManyOfManyPoll } from '@tunasong/schemas'
import {ConfirmDialog, getEntityPath, useNavigate, ListItemIcon, ListItemText, ListItemButton, capitalize} from '@tunasong/ui-lib'
import React, { useCallback, useState } from 'react'
import type { FC } from 'react'
import invariant from 'tiny-invariant'

/** Action to create playlist from songs and folders */
const CreatePollAction: FC<RenderMenuItemProps> = props => {
  const { element, onClose, ...restProps } = props
  const [showConfirm, setShowConfirm] = useState(false)
  const handleShowConfirm = useCallback(() => setShowConfirm(true), [])
  const navigate = useNavigate()
  const { createEntity } = graphHooks.useEntityCreate()
  const handleHideConfirm = useCallback(
    (ev: React.SyntheticEvent) => {
      ev.stopPropagation()
      setShowConfirm(false)
      if (onClose) {
        onClose()
      }
    },
    [onClose]
  )
  const { entity: parent } = graphHooks.useEntity(isEntity(element) ? element.parentId : null)

  const { entities } = graphHooks.useEntitiesById(isPlayList(element) ? element.mediaIds : [])

  const handleCreate = useCallback(
    async (ev: React.SyntheticEvent) => {
      ev.preventDefault()
      const name = `${element.name} Poll`
      /** Create a playlist as a sibling */
      invariant(parent && isEntity(element) && isPersisted(element), 'element must be persisted')

      const poll = {
        parentId: parent.id,
        type: 'poll',
        pollType: 'many-of-many',
        pollOptions: entities.map(e => ({
          name: e.name ?? 'Untitled',
          entityRef: e.id,
        })),
        /** @todo make configurable */
        responseQuota: 10,
        name,
      } satisfies ManyOfManyPoll
      const playlistEntity = await createEntity({ entity: poll, parent })
      await navigate(getEntityPath({ entity: playlistEntity }))

      handleHideConfirm(ev)
    },
    [createEntity, element, entities, handleHideConfirm, navigate, parent]
  )

  /** We support creating playlists for these entity types */
  if (!isPlayList(element)) {
    return null
  }

  const disabled = element.mediaIds.length === 0

  return (
    <ListItemButton {...restProps} disabled={disabled} onClick={handleShowConfirm} title={'Create a poll'}>
      <ListItemIcon>
        <PlayListIcon />
      </ListItemIcon>
      <ListItemText>Create Poll</ListItemText>
      <ConfirmDialog
        open={showConfirm}
        title="Create poll"
        text={`Create a poll for the songs of ${capitalize(
          element.type
        )}. The poll will placed as a sibling of this ${capitalize(element.type)}`}
        onCancel={handleHideConfirm}
        onConfirm={handleCreate}
      />
    </ListItemButton>
  )
}

export default CreatePollAction
